import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Mail, Moon, Sun } from "lucide-react";
import { useNavigate, Link, useLocation } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const alreadyNavigatedRef = useRef(false);

  // Persist dark mode
  useEffect(() => {
    const saved = localStorage.getItem("darkMode");
    if (saved) setDarkMode(saved === "true");
  }, []);
  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  // Redirect if already logged in
  useEffect(() => {
    if (sessionStorage.getItem("user_id") && !alreadyNavigatedRef.current) {
      alreadyNavigatedRef.current = true;
      navigate("/", { replace: true });
    }
  }, [navigate]);

  function validate() {
    if (!email) return "ایمیل را وارد کنید.";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) return "ایمیل نامعتبر است.";
    if (!password) return "رمز عبور را وارد کنید.";
    if (password.length < 6) return "رمز عبور باید حداقل 6 کاراکتر باشد.";
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) return setError(v);

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password: password.trim() }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "خطا در ورود");
      }

      // Save session
      sessionStorage.setItem("user_id", data.user_id);
      if (data.profile) {
        sessionStorage.setItem("user_profile", JSON.stringify(data.profile));
      }

      // Navigate safely
      if (!alreadyNavigatedRef.current) {
        alreadyNavigatedRef.current = true;
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "خطای شبکه. دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-500 ${
        darkMode
          ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
          : "bg-gradient-to-br from-blue-100 via-blue-200 to-blue-300"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full max-w-md rounded-2xl p-8 shadow-xl border transition-colors duration-500 ${
          darkMode
            ? "bg-slate-800/80 border-slate-700 text-white"
            : "bg-white/80 border-white/50 text-slate-900"
        } backdrop-blur-md`}
      >
        {/* Dark Mode Toggle */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setDarkMode((prev) => !prev)}
            className={`p-2 rounded-full transition-colors duration-300 ${
              darkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-200 hover:bg-slate-300"
            }`}
            aria-label="تغییر حالت شب/روز"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold">
            {darkMode ? "خوش آمدید 🌙" : "خوش آمدید ☀️"}
          </h1>
          <p className={`mt-1 text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
            برای ادامه وارد حساب خود شوید
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`text-sm border p-3 rounded-md ${
                darkMode
                  ? "text-red-300 bg-red-900/30 border-red-800"
                  : "text-red-600 bg-red-50 border-red-100"
              }`}
            >
              {error}
            </motion.div>
          )}

          {/* Email */}
          <label className="block">
            <span className={`text-sm flex items-center gap-2 font-medium ${darkMode ? "text-slate-200" : "text-sky-900"}`}>
              <Mail size={16} /> ایمیل
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 block w-full rounded-lg border px-3 py-2 shadow-sm focus:outline-none focus:ring-2 transition ${
                darkMode
                  ? "bg-slate-700 border-slate-600 text-white focus:ring-slate-500"
                  : "bg-white border-slate-300 text-slate-900 focus:ring-sky-300"
              }`}
              placeholder="you@example.com"
            />
          </label>

          {/* Password */}
          <label className="block relative">
            <span className={`text-sm flex items-center gap-2 font-medium ${darkMode ? "text-slate-200" : "text-sky-900"}`}>
              <Lock size={16} /> رمز عبور
            </span>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`mt-1 block w-full rounded-lg border px-3 py-2 pr-10 shadow-sm focus:outline-none focus:ring-2 transition ${
                darkMode
                  ? "bg-slate-700 border-slate-600 text-white focus:ring-slate-500"
                  : "bg-white border-slate-300 text-slate-900 focus:ring-sky-300"
              }`}
              placeholder="حداقل 6 کاراکتر"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "مخفی کردن رمز عبور" : "نمایش رمز عبور"}
              className={`absolute right-3 top-9 p-1 rounded-md transition-colors ${
                darkMode ? "hover:bg-slate-600" : "hover:bg-slate-100"
              }`}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </label>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className={`w-full rounded-xl py-2 font-medium shadow-md disabled:opacity-60 transition-colors ${
              darkMode
                ? "bg-slate-600 hover:bg-slate-500 text-white"
                : "bg-sky-600 hover:bg-sky-700 text-white"
            }`}
          >
            {loading ? "در حال ورود..." : "ورود"}
          </motion.button>
        </form>

        {/* Footer */}
        <div className={`mt-6 text-center text-sm ${darkMode ? "text-slate-300" : "text-slate-500"}`}>
          حساب ندارید؟{" "}
          <Link
            to="/signup"
            className={`font-medium ${darkMode ? "text-sky-400 hover:underline" : "text-sky-700 hover:underline"}`}
          >
            ثبت‌نام کنید
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
